import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { Typography } from "@mui/material";

import Loader from "components/Atoms/SharedComponents/Loader/Loader";
import setAuthToken from "services/modules/utils/handel_api";
import { useThemePalette } from "common/hooks/theme_palette";
import MeritApiLogsTemplate from "templates/merit/api-logs";
import {
  getMeritApiLogDetailsRequest,
  getMeritApiLogsRequest,
} from "services/modules/merit";
import {
  buildMeritApiLogsParams,
  getLogEndpoint,
  getLogMethod,
  getLogOrderReference,
  getLogStatusCode,
  isLogFailed,
} from "templates/merit/utils";

function MeritApiLogsPage() {
  const { t } = useTranslation();
  const { backgroundGreen, backgroundRed, textGreen, textRed } =
    useThemePalette();
  const token = localStorage.getItem("token");
  setAuthToken(token);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const type = searchParams.get("type");

  const list = useSelector((state) => state.merit.apiLogs);
  const count = useSelector((state) => state.merit.apiLogsCount);
  const details = useSelector((state) => state.merit.apiLogDetails);
  const load = useSelector((state) => state.merit.load);

  const [selectedRow, setSelectedRow] = useState({});
  const [page, setPage] = useState(1);
  const [filterObj, setFilterObj] = useState(null);

  const handlePageChange = (nextPage) => {
    setPage(nextPage);
    setFilterObj((prev) => ({
      ...prev,
      page_number: nextPage,
    }));
  };

  const getDetails = () => {
    dispatch(
      getMeritApiLogDetailsRequest({
        id: selectedRow?.rowData?.id ?? id,
      }),
    );
  };

  const getList = (filters) => {
    dispatch(
      getMeritApiLogsRequest({
        params: buildMeritApiLogsParams(filters),
      }),
    );
  };

  const renderResultBadge = (log) => {
    const failed = isLogFailed(log);

    return (
      <Typography
        sx={{
          bgcolor: failed ? backgroundRed : backgroundGreen,
          color: failed ? textRed : textGreen,
          p: "1px 10px",
          width: "fit-content",
          borderRadius: "4px",
        }}
      >
        {failed ? t("merit_api_logs.failed") : t("merit_api_logs.success")}
      </Typography>
    );
  };

  const mappedData = list?.map((data) => ({
    ...data,
    endpoint_display: getLogEndpoint(data),
    method_display: getLogMethod(data),
    status_code_display: getLogStatusCode(data),
    order_reference_display: getLogOrderReference(data),
    result_display: renderResultBadge(data),
    date: (
      <>
        {data?.created_at
          ? dayjs(data.created_at).format("DD-MM-YYYY, hh a")
          : "-"}
      </>
    ),
  }));

  const rowData = (data) => {
    setSelectedRow(data);
  };

  const breadcrumbs = [
    {
      active: true,
      path: "/",
      title: t("common.home"),
    },
    {
      active: !type,
      path: "/merit/api-logs",
      title: t("merit_api_logs.title"),
    },
    type === "details" && {
      active: false,
      path: "",
      title: t("merit_api_logs.log_details"),
    },
  ].filter(Boolean);

  const filterList = [
    {
      type: "filter",
      placeholder: t("merit_api_logs.filter_result"),
      options: [
        { id: "", name: t("merit_api_logs.all_logs") },
        { id: "failed", name: t("merit_api_logs.failed_only") },
      ],
      onChange: (value) =>
        setFilterObj((prev) => {
          const next = { ...prev, page_number: 1 };

          if (value?.value === "failed") {
            next.failed_only = true;
          } else {
            delete next.failed_only;
          }

          return next;
        }),
    },
  ];

  const actionsList = ({ data }) => [
    {
      condition: () => true,
      click: () => navigate(`?type=details&id=${data?.id}`),
      text: t("common.view"),
    },
  ];

  useEffect(() => {
    getList(filterObj || {});
  }, [filterObj]);

  useEffect(() => {
    if (id && pathname?.includes("merit/api-logs")) {
      getDetails();
    }
  }, [id, pathname]);

  return (
    <>
      <Loader open={load?.loading && load?.type === "list api logs"} />
      <MeritApiLogsTemplate
        data={mappedData}
        count={count}
        details={details}
        paginationPage={page}
        handlePageChange={handlePageChange}
        rowData={rowData}
        breadcrumbs={breadcrumbs}
        filterList={filterList}
        actionsList={actionsList}
        setFilterObj={setFilterObj}
      />
    </>
  );
}

export default MeritApiLogsPage;
